import { useState, useEffect } from "react";
import { Landmark, RefreshCw, Star, TrendingUp, TrendingDown } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const TAG_STYLES = {
  TOP_PICK: { label: "TOP PICK LT", bg: "bg-emerald-500/20", border: "border-emerald-400/50", text: "text-emerald-300" },
  DEFENSIVE_GROWTH: { label: "DEFENSIVE GROWTH", bg: "bg-cyan-500/15", border: "border-cyan-400/40", text: "text-cyan-300" },
  TURNAROUND: { label: "TURNAROUND", bg: "bg-amber-500/15", border: "border-amber-400/40", text: "text-amber-300" },
  SPECULATIVE: { label: "SPÉCULATIF", bg: "bg-rose-500/10", border: "border-rose-400/30", text: "text-rose-300" },
};

function fmtUSD(n) {
  if (!n) return "—";
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  return `$${n.toFixed(0)}`;
}

function Stars({ n }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={11}
          className={i <= n ? "fill-yellow-400 text-yellow-400" : "text-slate-600"}
        />
      ))}
    </div>
  );
}

export default function USGovernmentHoldings({ onTickerClick, portfolioTickers = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/government/us-holdings`, { timeout: 20000 });
      setData(r.data);
    } catch (e) {
      console.error("Gov holdings fetch error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const ownedSet = new Set((portfolioTickers || []).map((t) => (t || "").toUpperCase()));

  return (
    <div className="glass-card rounded-xl border border-indigo-500/20 overflow-hidden" data-testid="gov-holdings-panel">
      <div className="bg-gradient-to-r from-indigo-600/15 via-purple-600/10 to-transparent px-5 py-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-500/20 border border-indigo-400/30">
            <Landmark size={18} className="text-indigo-300" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base">US Government Holdings</h3>
            <p className="text-slate-400 text-xs">Participations directes du gouvernement américain dans des sociétés cotées</p>
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-2 rounded-lg bg-slate-700/60 hover:bg-slate-600 text-slate-300 transition disabled:opacity-50"
          data-testid="gov-holdings-refresh-btn"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading && !data && (
        <div className="p-6 text-center text-slate-400 text-sm">Chargement…</div>
      )}

      {data && data.holdings && (
        <div className="divide-y divide-slate-700/60">
          {data.holdings.map((h) => {
            const style = TAG_STYLES[h.tag] || TAG_STYLES.SPECULATIVE;
            const posChange = (h.change_pct || 0) >= 0;
            const owned = ownedSet.has((h.ticker || "").toUpperCase());
            return (
              <div
                key={h.ticker}
                className="p-4 hover:bg-slate-800/40 transition cursor-pointer"
                onClick={() => onTickerClick && onTickerClick(h.ticker)}
                data-testid={`gov-holding-${h.ticker}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-bold text-base">{h.ticker}</span>
                      <span className="text-slate-400 text-sm truncate">{h.name}</span>
                      {owned && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 font-medium">
                          HOLDING
                        </span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${style.bg} ${style.border} ${style.text}`}>
                        {style.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span>{h.sector}</span>
                      <span>•</span>
                      <span>{h.agency}</span>
                      <span>•</span>
                      <span>{h.announced}</span>
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    {h.current_price ? (
                      <>
                        <div className="text-white font-semibold text-sm">${h.current_price.toFixed(2)}</div>
                        <div className={`text-xs flex items-center gap-1 justify-end ${posChange ? "text-emerald-400" : "text-rose-400"}`}>
                          {posChange ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                          {posChange ? "+" : ""}{(h.change_pct || 0).toFixed(2)}%
                        </div>
                      </>
                    ) : (
                      <div className="text-slate-500 text-xs">n/a</div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 mt-2">
                  <div className="flex items-center gap-3 text-xs">
                    <div>
                      <span className="text-slate-500">Participation: </span>
                      <span className="text-indigo-300 font-semibold">{h.stake_pct?.toFixed(1)}%</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Valeur stake: </span>
                      <span className="text-slate-200 font-medium">{fmtUSD(h.stake_value_usd)}</span>
                    </div>
                  </div>
                  <Stars n={h.rating || 0} />
                </div>

                <p className="text-xs text-slate-400 mt-2 leading-relaxed">{h.thesis}</p>
              </div>
            );
          })}
        </div>
      )}

      {data && data.note && (
        <div className="px-4 py-2 text-[11px] text-slate-500 bg-slate-800/30 border-t border-slate-700/60">
          {data.note}
        </div>
      )}
    </div>
  );
}
